import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form"
import { RadioGroup, RadioGroupItem } from "./radio-group"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"
import { Info } from "lucide-react"
type RadioGroupTableProps<TFieldValues extends FieldValues> = {
  options: Array<{ value: string; label: string }>
  rows: Array<{ value: Path<TFieldValues>; label: string }>
  rowTooltips?: Record<Path<TFieldValues>, string>
}
export function RadioGroupTable<TFieldValues extends FieldValues>({
  options,
  rows,
  rowTooltips,
}: RadioGroupTableProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>()
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead></TableHead>
          {options.map((option) => (
            <TableHead
              key={option.value}
              id={option.value}
              className="text-center"
            >
              {option.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <Controller
            key={row.value}
            name={row.value}
            control={control}
            shouldUnregister
            render={({ field }) => (
              <RadioGroup
                name={row.value}
                onValueChange={field.onChange}
                asChild
                value={typeof field.value === "string" ? field.value : ""}
                className="table-row items-center"
                aria-labelledby={row.value}
              >
                <TableRow>
                  <RowLabel row={row} rowTooltips={rowTooltips} />
                  {options.map((option) => (
                    <TableCell className="text-center" key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        aria-labelledby={option.value}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </RadioGroup>
            )}
          />
        ))}
      </TableBody>
    </Table>
  )
}

function RowLabel<TFieldValues extends FieldValues>({
  row,
  rowTooltips,
}: Pick<RadioGroupTableProps<TFieldValues>, "rowTooltips"> & {
  row: { value: Path<TFieldValues>; label: string }
}) {
  const rowTooltip = rowTooltips?.[row.value]

  if (rowTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <TableCell
            id={row.value}
            className="[&_svg]:text-primary flex flex-row items-center gap-2 [&_svg]:size-4 [&_svg]:shrink-0"
          >
            {row.label}
            <Info />
          </TableCell>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">{rowTooltip}</TooltipContent>
      </Tooltip>
    )
  }

  return <TableCell id={row.value}>{row.label}</TableCell>
}
